"use client";

import {
  BookOpen,
  FileText,
  HelpCircle,
  PenSquare,
  PlayCircle,
  StickyNote,
  Volume2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  getRolePlayMode,
  getRolePlayModeLabelWithScore,
} from "@/lib/simulationMode";

// Outlined-pill colorway per role-play mode. Live Mic gets its own hue so it
// reads as distinct from a plain Practice assignment rather than collapsing in.
const ROLE_PLAY_MODE_PILL = {
  LIVE: "border-blue-200 bg-blue-50/60 text-blue-700",
  LIVE_MIC: "border-indigo-200 bg-indigo-50/60 text-indigo-700",
  PRACTICE: "border-gray-200 bg-gray-50 text-gray-600",
};

export const CONTENT_TYPE_ORDER = [
  "RESOURCE",
  "TEST",
  "ROLEPLAY",
  "STORY",
  "FAQ",
  "GLOSSARY",
];

const UNKNOWN_CONTENT_META = {
  icon: FileText,
  label: "Content",
  shortLabel: "Content",
  navLabel: "Content",
  pluralLabel: "Content",
  iconClass: "text-gray-500",
  completedText: "text-gray-600",
  tile: "bg-gray-100 text-gray-500",
  pickerSoft: "bg-gray-50 text-gray-500 border-gray-100",
  pickerCard: "border-gray-100 hover:border-gray-300 hover:ring-gray-200",
  pickerHoverBg: "hover:bg-gray-50",
  pill: "border-gray-200 bg-gray-50 text-gray-600",
};

// Single source of truth for learning-path content-type identity and color.
export const CONTENT_META = {
  RESOURCE: {
    icon: BookOpen,
    label: "Resource",
    shortLabel: "Resource",
    navLabel: "Resource",
    pluralLabel: "Resources",
    iconClass: "text-blue-600",
    completedText: "text-blue-600",
    tile: "bg-blue-50 text-blue-600",
    pickerSoft: "bg-blue-50 text-blue-600 border-blue-100",
    pickerCard: "border-blue-100 hover:border-blue-300 hover:ring-blue-200",
    pickerHoverBg: "hover:bg-blue-50/50",
    pill: "border-blue-200 bg-blue-50/60 text-blue-700",
  },
  TEST: {
    icon: FileText,
    label: "Knowledge test",
    shortLabel: "Test",
    navLabel: "Test",
    pluralLabel: "Tests",
    iconClass: "text-emerald-600",
    completedText: "text-emerald-600",
    tile: "bg-emerald-50 text-emerald-600",
    pickerSoft: "bg-emerald-50 text-emerald-600 border-emerald-100",
    pickerCard:
      "border-emerald-100 hover:border-emerald-300 hover:ring-emerald-200",
    pickerHoverBg: "hover:bg-emerald-50/50",
    pill: "border-emerald-200 bg-emerald-50/60 text-emerald-700",
  },
  ROLEPLAY: {
    icon: PlayCircle,
    label: "Assignment",
    shortLabel: "Assignment",
    navLabel: "Assignment",
    pluralLabel: "Assignments",
    iconClass: "text-violet-600",
    completedText: "text-violet-600",
    tile: "bg-violet-50 text-violet-600",
    pickerSoft: "bg-violet-50 text-violet-600 border-violet-100",
    pickerCard:
      "border-violet-100 hover:border-violet-300 hover:ring-violet-200",
    pickerHoverBg: "hover:bg-violet-50/50",
    pill: "border-violet-200 bg-violet-50/60 text-violet-700",
  },
  STORY: {
    icon: PenSquare,
    label: "Story assignment",
    shortLabel: "Story",
    navLabel: "Story Assignment",
    pluralLabel: "Stories",
    iconClass: "text-amber-600",
    completedText: "text-amber-600",
    tile: "bg-amber-50 text-amber-600",
    pickerSoft: "bg-amber-50 text-amber-600 border-amber-100",
    pickerCard:
      "border-amber-100 hover:border-amber-300 hover:ring-amber-200",
    pickerHoverBg: "hover:bg-amber-50/50",
    pill: "border-amber-200 bg-amber-50/60 text-amber-700",
  },
  FAQ: {
    icon: HelpCircle,
    label: "Q&As",
    shortLabel: "Q&As",
    navLabel: "Q&As",
    pluralLabel: "Q&As",
    iconClass: "text-teal-600",
    completedText: "text-teal-600",
    tile: "bg-teal-50 text-teal-600",
    pickerSoft: "bg-teal-50 text-teal-600 border-teal-100",
    pickerCard: "border-teal-100 hover:border-teal-300 hover:ring-teal-200",
    pickerHoverBg: "hover:bg-teal-50/50",
    pill: "border-teal-200 bg-teal-50/60 text-teal-700",
  },
  GLOSSARY: {
    icon: Volume2,
    label: "Glossary",
    shortLabel: "Glossary",
    navLabel: "Glossary",
    pluralLabel: "Glossary",
    iconClass: "text-indigo-600",
    completedText: "text-indigo-600",
    tile: "bg-indigo-50 text-indigo-600",
    pickerSoft: "bg-indigo-50 text-indigo-600 border-indigo-100",
    pickerCard:
      "border-indigo-100 hover:border-indigo-300 hover:ring-indigo-200",
    pickerHoverBg: "hover:bg-indigo-50/50",
    pill: "border-indigo-200 bg-indigo-50/60 text-indigo-700",
  },
};

export function getContentMeta(type) {
  return CONTENT_META[type] || UNKNOWN_CONTENT_META;
}

export function ContentTypeIcon({
  type,
  status,
  completedOnly = false,
  iconSize = "h-3.5 w-3.5",
  className,
  title,
}) {
  const meta = getContentMeta(type);
  const Icon = meta.icon;
  const colorClass =
    completedOnly && status !== "COMPLETED"
      ? "text-gray-400"
      : meta.iconClass;

  return (
    <Icon
      className={cn(iconSize, colorClass, className)}
      title={title || meta.shortLabel}
    />
  );
}

export function ContentTypeBadge({
  type,
  status,
  completedOnly = false,
  className,
}) {
  const meta = getContentMeta(type);
  const colorClass =
    completedOnly && status !== "COMPLETED"
      ? "text-gray-400"
      : meta.completedText;

  return (
    <span
      className={cn(
        "text-[10px] font-medium uppercase tracking-wider",
        colorClass,
        className
      )}
    >
      {meta.navLabel}
    </span>
  );
}

/**
 * Tinted, rounded icon tile for a content item.
 * size/iconSize let callers use a smaller tile in the compact preview list.
 */
export function ContentTypeTile({
  type,
  size = "h-9 w-9",
  iconSize = "h-[18px] w-[18px]",
}) {
  const meta = getContentMeta(type);
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-lg",
        size,
        meta.tile
      )}
    >
      <Icon className={iconSize} strokeWidth={1.75} />
    </span>
  );
}

const PILL_BASE =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium whitespace-nowrap";

/**
 * Status pills for a role-play content item (scored/practice + mental state),
 * styled per the design guidelines' outlined-pill colorways.
 */
export function RolePlayPills({ rolePlay }) {
  if (!rolePlay) return null;
  const mode = getRolePlayMode(rolePlay);
  return (
    <>
      <span
        className={cn(PILL_BASE, ROLE_PLAY_MODE_PILL[mode] || ROLE_PLAY_MODE_PILL.PRACTICE)}
      >
        {getRolePlayModeLabelWithScore(rolePlay)}
      </span>
      {rolePlay.mentalState && (
        <span
          className={cn(
            PILL_BASE,
            "border-purple-200 bg-purple-50/60 text-purple-700"
          )}
        >
          {rolePlay.mentalState}
        </span>
      )}
    </>
  );
}

export { StickyNote };
